import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ROUTES } from '../../routes/path';
import { useLanguage } from '../../utils/LanguageContext';
import { useCart } from '../../utils/CartContext'; // ADDED
import { useAuth } from '../../utils/useAuth';
import api from '../../services/api';
import '../../styles/product-detail.css'; // ADDED

// Backend family name -> [translation key, sticker]
const FAMILY_META = {
    'Mevali daraxtlar': ['filter_fruit', 'cat-fruit'],
    'Manzarali daraxtlar': ['filter_decorative', 'cat-decorative'],
    'Manzarali gullar': ['map_cat_flowers', 'cat-flowers'],
    'Butalar': ['map_cat_bushes', 'cat-bushes'],
};

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const { addToCart } = useCart(); // ADDED
    const { isAuthenticated } = useAuth();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);

    const [qty, setQty] = useState(1);
    const [addedToCart, setAddedToCart] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0 });

        api
            .get(`product/${id}/`)
            .then(res => {
                setProduct(res.data);
            })
            .catch(err => {
                console.error('Error fetching product:', err);
                setProduct(null);
            })
            .finally(() => setLoading(false));
    }, [id]);

    /* ------------------ GUARDS ------------------ */

    if (loading) {
        return (
            <div className="pd-state loading-container">
                <div className="pd-spinner"><i className="fas fa-seedling"></i></div>
                <p>{t('prod_loading')}</p>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="pd-state not-found-container">
                <img className="not-found-sticker" src="/images/stickers/signpost.webp" alt="" />
                <h2>{t('prod_not_found')}</h2>
                <p>{t('prod_not_found_desc')}</p>
                <Link to={`/${ROUTES.SHOP}`} className="btn-back-to-shop">
                    <i className="fas fa-arrow-left"></i> {t('prod_back')}
                </Link>
            </div>
        );
    }

    /* ------------------ DATA ------------------ */

    const basePrice = parseFloat(product.price) || 0;

    // UPDATED: Now uses CartContext for real-time updates
    const handleAdd = async (e) => {
        e.preventDefault();

        if (!isAuthenticated) {
            navigate('/auth?mode=login');
            return;
        }

        const success = await addToCart(product.id, parseInt(qty));

        if (success) {
            setAddedToCart(true);
            setTimeout(() => navigate(`/${ROUTES.CART}`), 1000);
        }
    };

    /* ------------------ RENDER ------------------ */

    return (
        <div className="product-detail-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/">{t('nav_home')}</Link>
                <i className="fas fa-chevron-right separator"></i>
                <Link to={`/${ROUTES.SHOP}`}>{t('nav_shop')}</Link>
                <i className="fas fa-chevron-right separator"></i>
                <span className="current">{product.tree?.[`name_${language}`] || product.tree?.name_en}</span>
            </div>

            <div className="product-detail-container">
                {/* LEFT - Image Gallery */}
                <div className="product-gallery">
                    <div className="main-image-wrapper">
                        {product.tree?.image ? (
                            <img
                                src={product.tree.image}
                                alt={product.tree?.[`name_${language}`] || product.tree?.name_en || ''}
                                className="main-image"
                            />
                        ) : (
                            <div className="placeholder-image">
                                <span className="placeholder-icon">🌳</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT - Product Info */}
                <div className="product-info-panel">
                    {/* Category Badge */}
                    {(() => {
                        const family = product.tree?.family?.name;
                        const [labelKey, sticker] = FAMILY_META[family] || [null, 'sapling-pin'];
                        return (
                            <div className="category-badge">
                                <img src={`/images/stickers/${sticker}.webp`} alt="" />
                                {labelKey ? t(labelKey) : family}
                            </div>
                        );
                    })()}

                    {/* Product Name */}
                    <h1 className="product-title">
                        {product.tree?.[`name_${language}`] || product.tree?.name_en}
                    </h1>

                    {/* Scientific Name */}
                    {product.tree?.scientific_name && (
                        <p className="scientific-name">
                            <i>{product.tree.scientific_name}</i>
                        </p>
                    )}

                    {/* Description */}
                    <div className="product-description">
                        <p>{product[`desc_${language}`] || product.desc_en || t('prod_desc_fallback')}</p>
                    </div>

                    {/* Price */}
                    <div className="price-section">
                        <div className="price-label">{t('prod_price')}</div>
                        <div className="price-value">
                            {basePrice.toLocaleString()}<span className="currency"> UZS</span>
                        </div>
                    </div>


                    {/* Quantity Selector */}
                    <div className="option-group">
                        <label className="option-label">
                            <i className="fas fa-sort-numeric-up"></i>
                            {t('prod_qty')}
                        </label>
                        <div className="quantity-selector">
                            <button
                                onClick={() => setQty(Math.max(1, qty - 1))}
                                className="qty-btn"
                            >
                                <i className="fas fa-minus"></i>
                            </button>
                            <span className="qty-display">{qty}</span>
                            <button
                                onClick={() => setQty(qty + 1)}
                                className="qty-btn"
                            >
                                <i className="fas fa-plus"></i>
                            </button>
                        </div>
                    </div>

                    {/* Add to Cart Button */}
                    <button
                        onClick={handleAdd}
                        disabled={addedToCart}
                        className={`btn-add-to-cart ${addedToCart ? 'added' : ''}`}
                    >
                        {addedToCart ? (
                            <>
                                <i className="fas fa-check"></i>
                                {t('shop_btn_added')}
                            </>
                        ) : (
                            <>
                                <i className="fas fa-shopping-cart"></i>
                                {t('prod_add_cart')}
                            </>
                        )}
                    </button>

                    {/* Additional Info */}
                    <div className="additional-info">
                        <div className="info-item">
                            <i className="fas fa-truck"></i>
                            <div>
                                <strong>{t('prod_info_delivery')}</strong>
                                <p>{t('prod_info_delivery_desc')}</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <i className="fas fa-shield-alt"></i>
                            <div>
                                <strong>{t('prod_info_quality')}</strong>
                                <p>{t('prod_info_quality_desc')}</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <i className="fas fa-headset"></i>
                            <div>
                                <strong>{t('prod_info_support')}</strong>
                                <p>{t('prod_info_support_desc')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}